import type { Point, Rect, Segment } from '@/types/book';

/** 计算两点距离；任一点为空时返回 Infinity 方便调用方跳过无效交点。 */
export function distance(pointA: Point | null, pointB: Point | null): number {
  if (pointA === null || pointB === null) return Infinity;
  return Math.hypot(pointB.x - pointA.x, pointB.y - pointA.y);
}

/** 计算两条线段所在直线的夹角弧度。 */
export function angleBetweenLines(lineA: Segment, lineB: Segment): number {
  const a1 = lineA[0].y - lineA[1].y;
  const a2 = lineB[0].y - lineB[1].y;
  const b1 = lineA[1].x - lineA[0].x;
  const b2 = lineB[1].x - lineB[0].x;

  return Math.acos((a1 * a2 + b1 * b2) / (Math.hypot(a1, b1) * Math.hypot(a2, b2)));
}

/** 判断点是否落在矩形内，落在内部则返回原点，否则返回 null。 */
export function pointInRect(rect: Rect, point: Point | null): Point | null {
  if (point === null) return null;

  if (
    point.x >= rect.left &&
    point.x <= rect.left + rect.width &&
    point.y >= rect.top &&
    point.y <= rect.top + rect.height
  ) {
    return point;
  }

  return null;
}

/** 将点围绕指定原点旋转指定弧度，返回旋转后的坐标。 */
export function rotatePoint(point: Point, origin: Point, angle: number): Point {
  return {
    x: point.x * Math.cos(angle) + point.y * Math.sin(angle) + origin.x,
    y: point.y * Math.cos(angle) - point.x * Math.sin(angle) + origin.y
  };
}

/** 将点限制在以 origin 为圆心、radius 为半径的圆内。 */
export function limitPointToCircle(origin: Point, radius: number, point: Point): Point {
  if (distance(origin, point) <= radius) return point;

  const a = origin.x;
  const b = origin.y;
  const n = point.x;
  const m = point.y;

  let x = Math.sqrt((radius ** 2 * (a - n) ** 2) / ((a - n) ** 2 + (b - m) ** 2)) + a;
  if (point.x < 0) x *= -1;

  let y = ((x - a) * (b - m)) / (a - n) + b;
  if (a - n + b === 0) y = radius;

  return { x, y };
}

/** 返回严格竖直折叠时，折痕与页面上下边的两个交点。 */
export function getVerticalFoldCrease(
  pageWidth: number,
  pageHeight: number,
  movedCornerX: number
): Segment {
  const creaseX = (pageWidth + movedCornerX) / 2;
  return [
    { x: creaseX, y: 0 },
    { x: creaseX, y: pageHeight }
  ];
}

/** 计算两条无限直线的交点，平行时返回 null。 */
export function intersectLines(lineA: Segment, lineB: Segment): Point | null {
  const a1 = lineA[0].y - lineA[1].y;
  const a2 = lineB[0].y - lineB[1].y;
  const b1 = lineA[1].x - lineA[0].x;
  const b2 = lineB[1].x - lineB[0].x;
  const c1 = lineA[0].x * lineA[1].y - lineA[1].x * lineA[0].y;
  const c2 = lineB[0].x * lineB[1].y - lineB[1].x * lineB[0].y;

  const det1 = a1 * c2 - a2 * c1;
  const det2 = b1 * c2 - b2 * c1;
  const divisor = a1 * b2 - a2 * b1;

  const x = -((c1 * b2 - c2 * b1) / divisor);
  const y = -((a1 * c2 - a2 * c1) / divisor);

  if (Number.isFinite(x) && Number.isFinite(y)) return { x, y };
  if (Math.abs(det1 - det2) < 0.1) throw new Error('Segment included');

  return null;
}

/** 计算两条线段所在直线的交点，并要求交点落在给定矩形内。 */
export function intersectSegmentsWithinRect(
  rect: Rect,
  lineA: Segment,
  lineB: Segment
): Point | null {
  return pointInRect(rect, intersectLines(lineA, lineB));
}

/** 在两点之间按指定步数生成包含首尾点的插值点列表。 */
export function pointsBetween(pointA: Point, pointB: Point, steps: number): Point[] {
  const count = Math.max(1, Math.floor(steps));
  const result: Point[] = [];

  for (let index = 0; index <= count; index += 1) {
    const t = index / count;
    result.push({
      x: pointA.x + (pointB.x - pointA.x) * t,
      y: pointA.y + (pointB.y - pointA.y) * t
    });
  }

  return result;
}
